import { useEffect, useRef } from 'react'

/**
 * Custom hook to toggle an element's fullscreen state.
 * @param {any} toggleSignal - The state/prop that triggers the fullscreen toggle when changed.
 * @returns {React.MutableRefObject} - The ref to attach to the target DOM element.
 */
export const useFullScreen = (toggleSignal) => {
  const elementRef = useRef(null)

  // Store the initial value of the signal so we can compare it later
  const prevSignal = useRef(toggleSignal)

  useEffect(() => {
    // 1. If the signal hasn't ACTUALLY changed, do nothing.
    // This perfectly prevents the "first load" and "random re-render" bugs.
    if (toggleSignal === prevSignal.current) {
      return
    }

    // 2. Update the ref so we know what the new current state is
    prevSignal.current = toggleSignal

    const elem = elementRef.current
    if (!elem) return

    // 3. Safe async function to handle browser fullscreen API
    const toggleFullscreen = async () => {
      try {
        if (!document.fullscreenElement) {
          // If not in fullscreen, enter it
          await elem.requestFullscreen()
        } else {
          // If already in fullscreen, clicking the button again exits it
          if (document.exitFullscreen) {
            await document.exitFullscreen()
          }
        }
      } catch (err) {
        console.error(`Error attempting to toggle fullscreen: ${err.message}`)
      }
    }

    toggleFullscreen()
  }, [toggleSignal])

  return elementRef
}
